import React from "react";
import { Link } from "react-router-dom";
import Breadcrumbs from "../components/Breadcrumbs";
import Product from "../components/Product";
import QuoteForm from "../components/QuoteForm";
import Specifications from "../components/Specifications";

// Import data from helpers
import { nitrileGlovesProducts } from "../helpers/nitrile-gloves/products";
import { nitrileGlovesFormFields } from "../helpers/nitrile-gloves/formConfig";
import { nitrileGlovesSpecifications } from "../helpers/nitrile-gloves/specifications";

const NitrileGloves: React.FC = () => {
  // Breadcrumb data
  const breadcrumbItems = [
    {
      label: "Nitrile Gloves",
      path: "/nitrile-gloves.html",
    },
  ];

  return (
    <div className="min-h-screen flex flex-col nitrile-page">
      <main className="flex-grow">
        {/* Breadcrumbs */}
        <Breadcrumbs items={breadcrumbItems} />

        {/* Page Title and Products - Unified Container */}
        <div className="w-full py-8 bg-white">
          <div className="page-container">
            <h1 className="page-title">
              Nitrile Disposable Gloves in Bulk
            </h1>
            <p className="page-description">
              <strong>Minimum Quantity: 10 Boxes (100 Gloves/Box)</strong>
            </p>
            <p className="page-description">
              Our premium powder free Nitrile gloves are also non-latex and
              available in multiple sizes, thicknesses, and colors. These high
              quality gloves are designed for long-lasting durability and
              multiple use applications. With enhanced strength and tear
              resistance, they offer unmatched reliability & protection. Simply
              request a quote below and we'll send our best pricing for our FDA
              and CE certified wholesale Nitrile gloves.
            </p>
            
            {/* Product Grid */}
            <div className="mt-8">
              <Product products={nitrileGlovesProducts} aspectRatio="nitrile" />
            </div>
          </div>
        </div>

        {/* Get a Quick Quote Form */}
        <QuoteForm
          title="Get a Quick Quote"
          description="To get started, enter your contact info and nitrile glove project details:"
          fields={nitrileGlovesFormFields}
          formName="Nitrile Gloves PPE.us"
          submitButtonText="Request Quote"
        />

        {/* Specifications */}
        <Specifications
          title="Specifications"
          items={nitrileGlovesSpecifications}
        />

        {/* Special Boxes Section */}
        <div className="w-full py-8 bg-white">
          <div className="px-5 nitrile-boxes ml-0 md:ml-[3%]">
            {/* Responsive grid for the 4 boxes */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              {/* Sizes Available */}
              <div
                className="p-6 rounded-lg bg-cyan-50"
                style={{ backgroundColor: "#D3F0F6" }}
              >
                <h3
                  className="text-lg font-bold text-gray-800 mb-4 inline-block border-b-2"
                  style={{ borderBottomColor: "#BB372E" }}
                >
                  Sizes Available
                </h3>
                <ul className="text-base text-gray-600 space-y-1 mt-4">
                  <li>XS</li>
                  <li>S</li>
                  <li>M</li>
                  <li>L</li>
                  <li>XL</li>
                  <li>XXL</li>
                </ul>
              </div>

              {/* Colors Available */}
              <div
                className="p-6 rounded-lg bg-cyan-50"
                style={{ backgroundColor: "#D3F0F6" }}
              >
                <h3
                  className="text-lg font-bold text-gray-800 mb-4 inline-block border-b-2"
                  style={{ borderBottomColor: "#BB372E" }}
                >
                  Colors Available
                </h3>
                <ul className="text-base text-gray-600 space-y-1 mt-4">
                  <li>Blue</li>
                  <li>Light Blue</li>
                  <li>Purple</li>
                  <li>Black</li>
                </ul>
              </div>

              {/* Glove Thickness Available */}
              <div
                className="p-6 rounded-lg bg-cyan-50"
                style={{ backgroundColor: "#D3F0F6" }}
              >
                <h3
                  className="text-lg font-bold text-gray-800 mb-4 inline-block border-b-2"
                  style={{ borderBottomColor: "#BB372E" }}
                >
                  Glove Thickness Available
                </h3>
                <ul className="text-base text-gray-600 space-y-1 mt-4">
                  <li>Under 4 mil</li>
                  <li>4 mil</li>
                  <li>5 mil</li>
                  <li>6 mil</li>
                  <li>Above 6 mil</li>
                </ul>
              </div>

              {/* Packaging */}
              <div
                className="p-6 rounded-lg bg-cyan-50"
                style={{ backgroundColor: "#D3F0F6" }}
              >
                <h3
                  className="text-lg font-bold text-gray-800 mb-4 inline-block border-b-2"
                  style={{ borderBottomColor: "#BB372E" }}
                >
                  Packaging
                </h3>
                <ul className="text-base text-gray-600 space-y-1 mt-4">
                  <li>100 gloves per box</li>
                </ul>
              </div>
            </div>

            {/* Border bottom */}
            <div className="border-b border-gray-200 mb-6"></div>

            {/* Other PPE Essentials */}
            <div className="text-left">
              <p className="text-base text-gray-600">
                <span className="other-ppe-essentials">Other PPE Essentials:</span>{" "}
                <span className="other-ppe-essentials-content">
                  <Link
                    to="/covid-19-test-kits.html"
                    className="text-blue-600 hover:text-blue-800 underline"
                  >
                    Test Kits
                  </Link>
                  {" | "}
                  <Link
                    to="/disposable-masks.html"
                    className="text-blue-600 hover:text-blue-800 underline"
                  >
                    Disposable Masks
                  </Link>
                  {" | "}
                  <Link
                    to="/surface-wipes.html"
                    className="text-blue-600 hover:text-blue-800 underline"
                  >
                    Surface Wipes
                  </Link>
                  {" | "}
                  <Link
                    to="/hand-sanitizer"
                    className="text-blue-600 hover:text-blue-800 underline"
                  >
                    Hand Sanitizer
                  </Link>
                </span>
              </p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default NitrileGloves;
